<template>
  <div>
    <a-page-header :title="pageTitle" @back="() => $router.back()">
      <template #extra v-if="isEdit && versionInfo">
        <a-space>
          <span style="color: #666;">编辑版本:</span>
          <a-select
            v-model:value="editingVersion"
            style="width: 320px"
            @change="handleVersionChange"
            :dropdown-match-select-width="false"
          >
            <a-select-option 
              v-for="v in versionHistory" 
              :key="v.version" 
              :value="v.version"
            >
              <div style="display: flex; justify-content: space-between; gap: 8px;">
                <span>
                  <strong>v{{ v.version }}</strong>
                  <a-tag v-if="v.is_published" color="green" size="small" style="margin-left: 4px;">生产环境</a-tag>
                  <a-tag v-if="v.is_latest" color="blue" size="small" style="margin-left: 4px;">预发布环境</a-tag>
                </span>
                <span style="color: #999; font-size: 12px;">{{ formatVersionTime(v.create_time) }}</span>
              </div>
              <div v-if="v.commit_message" style="font-size: 12px; color: #666; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 280px;">
                {{ v.commit_message }}
              </div>
            </a-select-option>
          </a-select>
          <a-tag v-if="versionInfo.has_unpublished_changes" color="orange">有未发布更改</a-tag>
          <a-divider type="vertical" />
          <router-link :to="{ name: 'knowledge-version-history', params: { id: $route.params.id } }">
            <a-button type="link" size="small">
              <HistoryOutlined /> 版本历史
            </a-button>
          </router-link>
        </a-space>
      </template>
      <template #footer v-if="isEdit && editingVersion && versionInfo && editingVersion !== versionInfo.latest_version">
        <a-alert 
          type="info" 
          show-icon
          message="您正在基于历史版本编辑"
          style="margin-bottom: 0;"
        >
          <template #description>
            保存后将创建新版本。如果只想发布 v{{ editingVersion }} 而不创建新版本，请前往
            <router-link :to="{ name: 'knowledge-version-history', params: { id: $route.params.id } }" style="color: #1890ff;">
              「版本历史」
            </router-link>
            页面操作。
          </template>
        </a-alert>
      </template>
    </a-page-header>
    
    <a-card style="margin-top: 16px">
      <a-form
        ref="formRef"
        :model="formData"
        :rules="rules"
        :label-col="{ span: 4 }"
        :wrapper-col="{ span: 20 }"
      >
        <a-form-item label="标题" name="title" required>
          <a-input v-model:value="formData.title" placeholder="请输入标题" />
        </a-form-item>

        <a-form-item label="知识类型" name="type_id" required>
          <a-radio-group v-model:value="formData.type_id" @change="handleTypeChange">
            <a-radio 
              v-for="type in knowledgeTypes" 
              :key="type.id" 
              :value="type.id"
              @click="handleTypeRadioClick(type.id)"
            >
              {{ type.type_name }}
            </a-radio>
          </a-radio-group>
          <a-button 
            v-if="formData.type_id" 
            type="link" 
            size="small" 
            style="margin-left: 8px; padding: 0"
            @click="handleClearType"
          >
            清除
          </a-button>
        </a-form-item>

        <TagSelector
          v-if="formData.type_id"
          :knowledge-type-code="currentKnowledgeTypeCode"
          :value="formData.tags"
          @change="handleTagsChange"
        />

        <a-form-item label="关联步骤">
          <div>
            <div style="margin-bottom: 8px; color: #666; font-size: 14px">选择步骤（可多选）：</div>
            <a-checkbox-group v-model:value="formData.step_ids" style="display: flex; flex-wrap: wrap; gap: 16px">
              <a-checkbox :value="1">步骤1</a-checkbox>
              <a-checkbox :value="2">步骤2</a-checkbox>
              <a-checkbox :value="3">步骤3</a-checkbox>
              <a-checkbox :value="4">步骤4</a-checkbox>
              <a-checkbox :value="5">步骤5</a-checkbox>
              <a-checkbox :value="6">步骤6</a-checkbox>
              <a-checkbox :value="7">步骤7</a-checkbox>
              <a-checkbox :value="8">步骤8</a-checkbox>
              <a-checkbox :value="9">步骤9（故障码生成）</a-checkbox>
            </a-checkbox-group>
            <div style="color: #999; font-size: 12px; margin-top: 8px">
              不选择表示所有步骤可调用
            </div>
          </div>
        </a-form-item>

        <a-form-item label="内容说明" name="content_summary">
          <a-textarea
            v-model:value="formData.content_summary"
            :rows="2"
            placeholder="请输入内容说明（对知识内容的简短介绍）"
            :maxlength="500"
            show-count
          />
        </a-form-item>

        <a-form-item label="内容" name="content">
          <div style="margin-bottom: 16px">
            <a-tabs
              v-model:activeKey="formData.content_format"
              type="card"
              @change="handleFormatChange"
              style="margin-bottom: 0"
            >
              <a-tab-pane key="text" tab="纯文本" />
              <a-tab-pane key="xml" tab="XML" />
              <a-tab-pane key="csv" tab="CSV" />
              <a-tab-pane key="md" tab="Markdown" />
            </a-tabs>
            <div style="display: inline-flex; align-items: center; margin-left: 8px">
              <a-upload
                :before-upload="handleFileUpload"
                :show-upload-list="false"
                accept=".txt,.xml,.md,.markdown,.csv,.xlsx,.xls"
              >
                <template #default>
                  <a-button type="primary">
                    <template #icon>
                      <UploadOutlined />
                    </template>
                    上传文件
                  </a-button>
                </template>
              </a-upload>
              <span style="margin-left: 8px; color: #999; font-size: 12px">
                上传文件内容会替换当前内容字段
              </span>
            </div>
          </div>
          <CsvTableEditor
            v-if="formData.content_format === 'csv'"
            v-model="formData.content"
          />
          <CodeEditor
            v-else
            v-model="formData.content"
            :language="formData.content_format"
            :height="400"
          />
        </a-form-item>

        <a-form-item label="状态" name="status">
          <a-radio-group v-model:value="formData.status">
            <a-radio :value="1">启用</a-radio>
            <a-radio :value="0">禁用</a-radio>
          </a-radio-group>
        </a-form-item>

        <a-form-item v-if="isEdit" label="版本说明" name="commit_message">
          <a-input
            v-model:value="formData.commit_message"
            placeholder="请输入本次修改的说明（可选）"
            :maxlength="200"
          />
        </a-form-item>

        <a-form-item :wrapper-col="{ offset: 4, span: 20 }">
          <a-space>
            <a-button type="primary" @click="handleSubmit(false)" :loading="loading">
              {{ isEdit ? '发布到预发布环境' : '保存' }}
            </a-button>
            <a-button v-if="isEdit" type="primary" @click="handleSubmit(true)" :loading="loading">
              发布到生产环境
            </a-button>
            <a-button @click="handleCancel">取消</a-button>
          </a-space>
        </a-form-item>
      </a-form>
    </a-card>
  </div>
</template>

<script>
import { ref, reactive, computed, onMounted, nextTick } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { message } from 'ant-design-vue';
import { UploadOutlined, HistoryOutlined } from '@ant-design/icons-vue';
import * as XLSX from 'xlsx';
import { getKnowledgeById, createKnowledge, updateKnowledge, getKnowledgeVersionHistory, getKnowledgeVersionDetail } from '../api/knowledge';
import { getKnowledgeTypes } from '../api/knowledgeType';
import TagSelector from '../components/TagSelector.vue';
import CodeEditor from '../components/CodeEditor.vue';
import CsvTableEditor from '../components/CsvTableEditor.vue';

export default {
  name: 'KnowledgeForm',
  components: {
    TagSelector,
    CodeEditor,
    CsvTableEditor,
    UploadOutlined,
    HistoryOutlined
  },
  setup() {
    const router = useRouter();
    const route = useRoute();
    const formRef = ref(null);
    const loading = ref(false);
    const isEdit = computed(() => !!route.params.id);
    const knowledgeTypes = ref([]);
    const versionInfo = ref(null); // 版本信息 { latest_version, published_version, has_unpublished_changes }
    const versionHistory = ref([]); // 版本历史列表
    const editingVersion = ref(null); // 当前编辑的版本号
    const isFillingFormData = ref(false); // 是否正在填充表单数据（用于防止标签被意外清空）

    const pageTitle = computed(() => {
      if (isEdit.value) {
        if (editingVersion.value && versionInfo.value && editingVersion.value !== versionInfo.value.latest_version) {
          return `编辑知识 (基于 v${editingVersion.value})`;
        }
        return '编辑知识';
      }
      return '新建知识';
    });

    // 格式化版本时间
    const formatVersionTime = (dateString) => {
      if (!dateString) return '';
      const date = new Date(dateString);
      return date.toLocaleDateString('zh-CN', {
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    };
    const callSchemes = ref([]); // AI调用流程列表
    const stepModalVisible = ref(false); // 步骤/Worker选择弹窗显示状态
    const currentScheme = ref(null); // 当前正在选择worker的流程
    const schemeStepsData = ref([]); // 当前流程的步骤数据
    const schemeStepsLoading = ref(false); // 步骤数据加载状态
    const tempSelectedWorkers = ref([]); // 弹窗中临时选中的worker [{ workerId, workerName, stepName, stepOrder }]
    const expandedSteps = ref(new Set()); // 展开的步骤ID集合
    const selectedWorkersByScheme = ref({}); // 按流程分组的已选worker { schemeId: { schemeName, workers: [...] } }
    const schemeWorkerIdsMap = ref({}); // 缓存每个流程下的worker ID列表 { schemeId: [workerId1, workerId2, ...] }

    const formData = reactive({
      type_id: undefined,
      step_ids: [],
      title: '',
      content_summary: '',
      content: '',
      content_format: 'text',
      status: 1,
      sort_order: 0,
      remark: '',
      tags: [],
      commit_message: ''
    });

    const currentKnowledgeTypeCode = computed(() => {
      const type = knowledgeTypes.value.find(t => t.id === formData.type_id);
      return type ? type.type_code : '';
    });

    const rules = {
      type_id: [{ required: true, message: '请选择知识类型', trigger: 'change' }],
      title: [{ required: true, message: '请输入标题', trigger: 'blur' }]
    };

    // 加载知识类型
    const loadKnowledgeTypes = async () => {
      try {
        const res = await getKnowledgeTypes();
        knowledgeTypes.value = res.data || [];
      } catch (error) {
        console.error('加载知识类型失败:', error);
      }
    };

    // 加载AI调用流程列表
    const loadCallSchemes = async () => {
      try {
        const res = await getCallSchemes();
        callSchemes.value = (res.data || []).sort((a, b) => a.id - b.id);
      } catch (error) {
        console.error('加载AI调用流程失败:', error);
      }
    };

    // 从worker ID列表恢复selectedWorkersByScheme（需要查询每个worker所属的流程和步骤）
    const restoreSelectedWorkers = async (workerIds) => {
      if (!workerIds || workerIds.length === 0) {
        selectedWorkersByScheme.value = {};
        return;
      }

      // 遍历所有流程，查找包含这些worker的流程
      for (const scheme of callSchemes.value) {
        try {
          const stepsRes = await getSchemeSteps(scheme.id);
          if (stepsRes.code === 200 && stepsRes.data) {
            const stepsData = stepsRes.data.steps || [];
            const workers = [];
            
            stepsData.forEach((step, index) => {
              if (step.sub_steps && step.sub_steps.length > 0) {
                step.sub_steps.forEach(worker => {
                  if (workerIds.includes(worker.id)) {
                    workers.push({
                      workerId: worker.id,
                      workerName: worker.step_name,
                      stepName: `步骤${index + 1}`,
                      stepOrder: index + 1
                    });
                  }
                });
              }
            });
            
            if (workers.length > 0) {
              selectedWorkersByScheme.value[scheme.id] = {
                schemeName: scheme.scheme_name,
                workers
              };
            }
          }
        } catch (error) {
          console.error(`加载流程 ${scheme.scheme_name} 的步骤失败:`, error);
        }
      }
    };

    // 加载知识详情
    const loadKnowledge = async () => {
      if (!isEdit.value) return;

      loading.value = true;
      try {
        const res = await getKnowledgeById(route.params.id);
        const data = res.data;
        
        // 保存版本信息
        versionInfo.value = {
          latest_version: data.latest_version || 1,
          published_version: data.published_version || 1,
          has_unpublished_changes: data.has_unpublished_changes || false
        };
        editingVersion.value = data.latest_version || 1;
        
        // 加载版本历史
        await loadVersionHistory();
        
        // 填充表单数据
        fillFormData(data);
      } catch (error) {
        message.error('加载数据失败');
        router.back();
      } finally {
        loading.value = false;
      }
    };

    // 加载版本历史
    const loadVersionHistory = async () => {
      try {
        const res = await getKnowledgeVersionHistory(route.params.id);
        versionHistory.value = res.data || [];
      } catch (error) {
        console.error('加载版本历史失败:', error);
      }
    };

    // 填充表单数据
    const fillFormData = (data) => {
      // 设置标志，防止 TagSelector 的 change 事件覆盖标签数据
      isFillingFormData.value = true;
      
      formData.type_id = data.type_id;
      formData.title = data.title;
      formData.content_summary = data.content_summary || '';
      formData.content = data.content || '';
      formData.content_format = data.content_format || detectContentFormat(data.content);
      formData.status = data.status;
      formData.sort_order = data.sort_order || 0;
      formData.remark = data.remark || '';
      formData.tags = data.tags || [];
      formData.commit_message = '';
      // 获取关联的步骤序号（step_order：1-9）
      const stepOrders = (data.steps || []).map(s => s.step_order).filter(so => so >= 1 && so <= 9);
      formData.step_ids = stepOrders;
      
      // 在 Vue 响应式更新完成后重置标志
      // 使用两个 nextTick 确保 TagSelector 的异步 watch 完全执行完毕
      nextTick(() => {
        nextTick(() => {
          isFillingFormData.value = false;
        });
      });
    };

    // 版本切换
    const handleVersionChange = async (version) => {
      loading.value = true;
      try {
        const res = await getKnowledgeVersionDetail(route.params.id, version);
        const data = res.data;
        fillFormData(data);
        message.info(`已加载 v${version} 版本的内容`);
      } catch (error) {
        message.error('加载版本内容失败');
        editingVersion.value = versionInfo.value.latest_version;
      } finally {
        loading.value = false;
      }
    };

    // 知识类型单选按钮点击处理（允许取消选择）
    const handleTypeRadioClick = (typeId) => {
      // 如果点击的是已选中的选项，则取消选择
      if (formData.type_id === typeId) {
        formData.type_id = undefined;
        handleTypeChange();
      }
    };

    // 清除知识类型
    const handleClearType = () => {
      formData.type_id = undefined;
      handleTypeChange();
    };

    // 知识类型变化
    const handleTypeChange = () => {
      // 清空标签
      formData.tags = [];
      // 注意：知识类型变化时不清空步骤，所有类型都可以关联步骤
      // 强制触发标签选择器的清空
      nextTick(() => {
        // 标签选择器会通过 watch 监听 knowledgeTypeCode 的变化自动清空
      });
    };

    // 点击流程标签：打开弹窗（按需加载步骤数据）
    const handleSchemeClick = async (scheme) => {
      currentScheme.value = scheme;
      
      // 加载该流程的步骤数据
      schemeStepsLoading.value = true;
      try {
        const stepsRes = await getSchemeSteps(scheme.id);
        if (stepsRes.code === 200 && stepsRes.data) {
          const stepsData = stepsRes.data.steps || [];
          // 按 step_order 排序
          schemeStepsData.value = stepsData.sort((a, b) => {
            const orderA = a.step_order || 0;
            const orderB = b.step_order || 0;
            if (orderA !== orderB) {
              return orderA - orderB;
            }
            return (a.call_step_id || a.id || 0) - (b.call_step_id || b.id || 0);
          });
          
          // 缓存该流程下的所有worker ID和worker信息
          const allWorkerIds = [];
          const workerInfoMap = {}; // { workerId: { stepName, stepOrder, workerName } }
          if (schemeStepsData.value.length > 0) {
            schemeStepsData.value.forEach((step, index) => {
              if (step.sub_steps && step.sub_steps.length > 0) {
                step.sub_steps.forEach(worker => {
                  allWorkerIds.push(worker.id);
                  workerInfoMap[worker.id] = {
                    stepName: `步骤${index + 1}`,
                    stepOrder: index + 1,
                    workerName: worker.step_name
                  };
                });
              }
            });
          }
          schemeWorkerIdsMap.value[scheme.id] = allWorkerIds;
          currentScheme.value.workerInfoMap = workerInfoMap;
          
          // 默认展开步骤1、2、3
          const defaultExpandedSteps = new Set();
          schemeStepsData.value.forEach((step, index) => {
            if (index < 3) { // 只展开前3个步骤（索引0、1、2）
              defaultExpandedSteps.add(step.call_step_id || step.id);
            }
          });
          expandedSteps.value = defaultExpandedSteps;
        } else {
          schemeStepsData.value = [];
          schemeWorkerIdsMap.value[scheme.id] = [];
        }
      } catch (error) {
        console.error(`加载流程 ${scheme.scheme_name} 的步骤失败:`, error);
        message.error(`加载步骤失败: ${scheme.scheme_name}`);
        schemeStepsData.value = [];
        schemeWorkerIdsMap.value[scheme.id] = [];
      } finally {
        schemeStepsLoading.value = false;
      }
      
      // 初始化弹窗中已选中的worker（从selectedWorkersByScheme中获取该流程的worker）
      const existingWorkers = selectedWorkersByScheme.value[scheme.id]?.workers || [];
      tempSelectedWorkers.value = existingWorkers.map(w => ({
        workerId: w.workerId,
        workerName: w.workerName,
        stepName: w.stepName,
        stepOrder: w.stepOrder
      }));
      
      stepModalVisible.value = true;
    };

    // 步骤展开/收起切换
    const toggleStepExpand = (stepId) => {
      if (expandedSteps.value.has(stepId)) {
        expandedSteps.value.delete(stepId);
      } else {
        expandedSteps.value.add(stepId);
      }
    };

    // 检查步骤是否展开
    const isStepExpanded = (stepId) => {
      return expandedSteps.value.has(stepId);
    };

    // 弹窗中worker切换
    const handleWorkerToggleInModal = (workerId, checked, stepName, stepOrder) => {
      if (checked) {
        // 添加worker
        const workerInfo = currentScheme.value.workerInfoMap[workerId];
        if (workerInfo) {
          tempSelectedWorkers.value.push({
            workerId,
            workerName: workerInfo.workerName,
            stepName: workerInfo.stepName,
            stepOrder: workerInfo.stepOrder
          });
        }
      } else {
        // 移除worker
        const index = tempSelectedWorkers.value.findIndex(w => w.workerId === workerId);
        if (index > -1) {
          tempSelectedWorkers.value.splice(index, 1);
        }
      }
    };

    // 检查弹窗中worker是否被选中
    const isWorkerSelectedInModal = (workerId) => {
      return tempSelectedWorkers.value.some(w => w.workerId === workerId);
    };

    // 弹窗确定：保存选中的worker
    const handleStepModalOk = () => {
      if (currentScheme.value) {
        const schemeId = currentScheme.value.id;
        const schemeName = currentScheme.value.scheme_name;
        
        // 更新该流程的已选worker
        if (tempSelectedWorkers.value.length > 0) {
          selectedWorkersByScheme.value[schemeId] = {
            schemeName,
            workers: [...tempSelectedWorkers.value]
          };
        } else {
          // 如果没有选中的worker，删除该流程的记录
          delete selectedWorkersByScheme.value[schemeId];
        }
        
        // 更新formData.step_ids（所有流程的worker ID数组）
        updateStepIds();
      }
      stepModalVisible.value = false;
      currentScheme.value = null;
      tempSelectedWorkers.value = [];
      expandedSteps.value = new Set();
    };

    // 弹窗取消
    const handleStepModalCancel = () => {
      stepModalVisible.value = false;
      currentScheme.value = null;
      tempSelectedWorkers.value = [];
      expandedSteps.value = new Set();
    };

    // 更新formData.step_ids（从selectedWorkersByScheme中提取所有worker ID）
    const updateStepIds = () => {
      const allWorkerIds = [];
      for (const schemeData of Object.values(selectedWorkersByScheme.value)) {
        if (schemeData.workers && schemeData.workers.length > 0) {
          allWorkerIds.push(...schemeData.workers.map(w => w.workerId));
        }
      }
      formData.step_ids = [...new Set(allWorkerIds)]; // 去重
    };

    // 获取流程下已选中的worker数量
    const getSchemeSelectedCount = (schemeId) => {
      const schemeData = selectedWorkersByScheme.value[schemeId];
      return schemeData ? schemeData.workers.length : 0;
    };

    // 删除已选择的worker
    const handleRemoveWorker = (schemeId, workerId) => {
      const schemeData = selectedWorkersByScheme.value[schemeId];
      if (schemeData) {
        const index = schemeData.workers.findIndex(w => w.workerId === workerId);
        if (index > -1) {
          schemeData.workers.splice(index, 1);
          // 如果该流程没有worker了，删除该流程的记录
          if (schemeData.workers.length === 0) {
            delete selectedWorkersByScheme.value[schemeId];
          }
        }
      }
      // 更新formData.step_ids
      updateStepIds();
    };

    // 标签变化
    const handleTagsChange = (tags) => {
      // 在填充表单数据期间，忽略 TagSelector 的 change 事件
      // 这是为了防止版本切换时，知识类型变化触发的清空操作覆盖正确的标签数据
      if (isFillingFormData.value) {
        return;
      }
      formData.tags = tags;
    };

    // 格式自动识别
    const detectContentFormat = (content) => {
      if (!content || typeof content !== 'string') {
        return 'text';
      }
      
      const trimmed = content.trim();
      
      // XML格式：包含标签结构
      if (trimmed.startsWith('<') && trimmed.includes('</')) {
        return 'xml';
      }
      
      // CSV格式：包含逗号分隔的多行数据
      if (trimmed.includes(',') && trimmed.split('\n').length > 1) {
        const lines = trimmed.split('\n').filter(line => line.trim());
        if (lines.length >= 2 && lines[0].split(',').length >= 2) {
          return 'csv';
        }
      }
      
      // Markdown格式：包含Markdown标记
      if (trimmed.includes('#') || trimmed.includes('*') || trimmed.includes('```') || trimmed.includes('**')) {
        return 'md';
      }
      
      return 'text';
    };

    // 格式变化
    const handleFormatChange = () => {
      // 如果内容为空，可以根据格式自动识别
      if (!formData.content && formData.content_format === 'text') {
        // 可以在这里添加自动识别逻辑
      }
    };

    // Excel 转 CSV 转换函数
    const convertExcelToCSV = (file) => {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => {
          try {
            const data = new Uint8Array(e.target.result);
            const workbook = XLSX.read(data, { type: 'array' });
            
            // 读取第一个工作表
            const firstSheetName = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[firstSheetName];
            
            // 转换为 CSV 格式
            const csv = XLSX.utils.sheet_to_csv(worksheet);
            resolve(csv);
          } catch (error) {
            reject(error);
          }
        };
        reader.onerror = () => {
          reject(new Error('文件读取失败'));
        };
        reader.readAsArrayBuffer(file);
      });
    };

    // 文件上传处理
    const handleFileUpload = async (file) => {
      // 验证文件类型
      const fileName = file.name.toLowerCase();
      const validExtensions = ['.txt', '.xml', '.md', '.markdown', '.csv', '.xlsx', '.xls'];
      const fileExtension = fileName.substring(fileName.lastIndexOf('.'));
      
      if (!validExtensions.includes(fileExtension)) {
        message.error('只支持上传 txt、xml、md、markdown、csv、xlsx、xls 格式的文件');
        return false; // 阻止上传
      }

      // 处理 Excel 文件
      if (fileExtension === '.xlsx' || fileExtension === '.xls') {
        try {
          const csvContent = await convertExcelToCSV(file);
          formData.content = csvContent;
          formData.content_format = 'csv';
          message.success('Excel 文件已转换为 CSV 格式');
        } catch (error) {
          console.error('Excel 转换失败:', error);
          message.error('Excel 文件转换失败，请检查文件格式');
        }
        return false; // 阻止默认上传行为
      }

      // 处理其他文件类型
      // 根据文件扩展名设置内容格式
      let format = 'text';
      if (fileExtension === '.xml') {
        format = 'xml';
      } else if (fileExtension === '.csv') {
        format = 'csv';
      } else if (fileExtension === '.md' || fileExtension === '.markdown') {
        format = 'md';
      } else if (fileExtension === '.txt') {
        format = 'text';
      }

      // 读取文件内容
      const reader = new FileReader();
      reader.onload = (e) => {
        const content = e.target.result;
        formData.content = content;
        formData.content_format = format;
        message.success('文件上传成功');
      };
      reader.onerror = () => {
        message.error('文件读取失败');
      };
      reader.readAsText(file, 'UTF-8');

      return false; // 阻止默认上传行为
    };

    // 提交（保存但不发布）
    const handleSubmit = async (publish = false) => {
      try {
        await formRef.value.validate();
        
        loading.value = true;
        const data = {
          ...formData,
          publish // 是否同时发布
        };
        
        // 如果内容格式未设置，自动识别
        if (!data.content_format || data.content_format === 'text') {
          data.content_format = detectContentFormat(data.content);
        }

        if (isEdit.value) {
          const result = await updateKnowledge(route.params.id, data);
          if (publish) {
            message.success('保存并发布成功');
          } else {
            message.success('已创建新版本');
          }
          // 重新加载数据以更新版本信息
          await loadKnowledge();
        } else {
          const result = await createKnowledge(data);
          message.success('创建成功');
          router.push({ name: 'knowledge' });
        }
      } catch (error) {
        if (error.errorFields) {
          return;
        }
        const errorMessage = error.response?.data?.message || error.message || (isEdit.value ? '更新失败' : '创建失败');
        message.error(errorMessage);
      } finally {
        loading.value = false;
      }
    };

    // 保存并发布
    const handleSubmitAndPublish = () => {
      handleSubmit(true);
    };

    // 取消
    const handleCancel = () => {
      router.back();
    };

    onMounted(async () => {
      await loadKnowledgeTypes();
      await loadKnowledge();
    });

    return {
      formRef,
      loading,
      isEdit,
      knowledgeTypes,
      callSchemes,
      stepModalVisible,
      currentScheme,
      schemeStepsData,
      schemeStepsLoading,
      selectedWorkersByScheme,
      formData,
      currentKnowledgeTypeCode,
      rules,
      versionInfo,
      versionHistory,
      editingVersion,
      pageTitle,
      formatVersionTime,
      handleTypeChange,
      handleTypeRadioClick,
      handleClearType,
      handleTagsChange,
      handleFormatChange,
      handleFileUpload,
      handleSubmit,
      handleSubmitAndPublish,
      handleCancel,
      handleVersionChange,
      handleSchemeClick,
      toggleStepExpand,
      isStepExpanded,
      handleWorkerToggleInModal,
      isWorkerSelectedInModal,
      handleStepModalOk,
      handleStepModalCancel,
      getSchemeSelectedCount,
      handleRemoveWorker
    };
  }
};
</script>

